export interface DriveInput {
  throttle: number;
  brake: number;
  steer: number;
  handbrake: boolean;
  /** Nitro. Held while the boost meter lasts. */
  boost: boolean;
}

const BLOCKED = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Space',
]);

interface VirtualKeys {
  throttle: boolean;
  brake: boolean;
  left: boolean;
  right: boolean;
  handbrake: boolean;
  boost: boolean;
}

export class Input {
  private readonly keys = new Set<string>();
  private readonly pressed = new Set<string>();
  private virtual: VirtualKeys = {
    throttle: false,
    brake: false,
    left: false,
    right: false,
    handbrake: false,
    boost: false,
  };
  private gamepadSteer = 0;
  private gamepadThrottle = 0;
  private gamepadBrake = 0;
  private gamepadHandbrake = false;
  private gamepadBoost = false;
  private gamepadPauseWas = false;
  gamepadPauseEdge = false;

  constructor() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.releaseAll);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.releaseAll();
    });
  }

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.repeat) return;
    this.keys.add(event.code);
    this.pressed.add(event.code);
    if (BLOCKED.has(event.code)) event.preventDefault();
  };

  private onKeyUp = (event: KeyboardEvent): void => {
    this.keys.delete(event.code);
  };

  releaseAll = (): void => {
    this.keys.clear();
    this.virtual = {
      throttle: false,
      brake: false,
      left: false,
      right: false,
      handbrake: false,
      boost: false,
    };
    this.gamepadThrottle = 0;
    this.gamepadBrake = 0;
    this.gamepadSteer = 0;
    this.gamepadHandbrake = false;
    this.gamepadBoost = false;
  };

  setVirtual(name: keyof VirtualKeys, down: boolean): void {
    this.virtual[name] = down;
  }

  consumePressed(code: string): boolean {
    if (!this.pressed.has(code)) return false;
    this.pressed.delete(code);
    return true;
  }

  pollGamepad(): void {
    this.gamepadPauseEdge = false;
    const pads = navigator.getGamepads?.();
    if (!pads) return;
    let steer = 0;
    let throttle = 0;
    let brake = 0;
    let handbrake = false;
    let boost = false;
    let pause = false;
    for (const pad of pads) {
      if (!pad) continue;
      const axis = pad.axes[0] ?? 0;
      if (Math.abs(axis) > 0.18) steer = axis;
      const rightTrigger = pad.buttons[7]?.value ?? 0;
      const leftTrigger = pad.buttons[6]?.value ?? 0;
      const south = pad.buttons[0]?.pressed ?? false;
      const east = pad.buttons[1]?.pressed ?? false;
      const accelBtn = pad.buttons[7]?.pressed || rightTrigger > 0.08 || (pad.buttons[5]?.pressed ?? false);
      const brakeBtn = leftTrigger > 0.08 || (pad.buttons[4]?.pressed ?? false);
      if (accelBtn) throttle = Math.max(throttle, rightTrigger || 1);
      if (brakeBtn) brake = Math.max(brake, leftTrigger || 1);
      if (south) handbrake = true;
      if (east) boost = true;
      if (pad.buttons[9]?.pressed) pause = true;
    }
    this.gamepadSteer = Math.max(-1, Math.min(1, steer));
    this.gamepadThrottle = throttle;
    this.gamepadBrake = brake;
    this.gamepadHandbrake = handbrake;
    this.gamepadBoost = boost;
    if (pause && !this.gamepadPauseWas) this.gamepadPauseEdge = true;
    this.gamepadPauseWas = pause;
  }

  drive(): DriveInput {
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft') || this.virtual.left;
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight') || this.virtual.right;
    let steer = 0;
    if (left !== right) steer = right ? 1 : -1;
    else if (Math.abs(this.gamepadSteer) > 0) steer = this.gamepadSteer;

    const throttle = this.keys.has('KeyW') || this.keys.has('ArrowUp') || this.virtual.throttle ? 1 : this.gamepadThrottle;
    const brake = this.keys.has('KeyS') || this.keys.has('ArrowDown') || this.virtual.brake ? 1 : this.gamepadBrake;
    const handbrake = this.keys.has('Space') || this.virtual.handbrake || this.gamepadHandbrake;
    const boost = this.keys.has('ShiftLeft') || this.keys.has('ShiftRight') || this.virtual.boost || this.gamepadBoost;

    return {
      throttle: brake > 0.2 ? Math.min(throttle, 0.15) : throttle,
      brake,
      steer,
      handbrake,
      boost,
    };
  }
}
